import { memo } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from "react-router-dom";
import LiveLayout from "Components/LiveLayout";
import LoginPage from "Components/LoginPage";
import PrivateRoute from "Components/Routing/PrivateRoute";
import PlaybackLayout from "Components/PlaybackLayout";
import ConfigLayout from "Components/ConfigLayout";
import LanguageSettings from "Components/ConfigLayout/System/LanguageSettings";
import CodecSettings from "Components/ConfigLayout/Camera/CodecSettings";
import TamperSettings from "Components/ConfigLayout/Camera/TamperSettings";
import SecuritySettings from "Components/ConfigLayout/System/SecuritySettings";
import ManagementSettings from "Components/ConfigLayout/System/ManagementSettings";
import UserManagement from "Components/ConfigLayout/User/UserManagement";
import GroupAuthority from "Components/ConfigLayout/User/GroupAuthority";
import UpnpSettings from "Components/ConfigLayout/Network/UpnpSettings";
import EmbeddedOsdSettings from "Components/ConfigLayout/Camera/EmbeddedOsdSettings";
import AudioSettings from "Components/ConfigLayout/Audio/AudioSettings";
import SystemInformation from "Components/ConfigLayout/System/SystemInformation";
import DateTimeSettings from "Components/ConfigLayout/System/DateTimeSettings";
import NetworkSecuritySettings from "Components/ConfigLayout/Network/SecuritySettings";
import IpSetupSettings from "Components/ConfigLayout/Network/IpSetupSettings";
import RecordSetupSettings from "Components/ConfigLayout/Record/SetupSettings";
import SystemEventSettings from "Components/ConfigLayout/Event/SystemEventSettings";
import SdCardSettings from "Components/ConfigLayout/Storage/SdCardSettings";
import MotionEventSettings from "Components/ConfigLayout/Event/MotionEventSettings";
import TamperEventSettings from "Components/ConfigLayout/Event/TamperEventSettings";
import NetworkLossSettings from "Components/ConfigLayout/Event/NetworkLossSettings";
import ActionSettings from "Components/ConfigLayout/Event/ActionSettings";
import AnalyticsSettings from "Components/ConfigLayout/Event/AnalyticsEventSettings";
import AlarmEventSettings from "Components/ConfigLayout/Event/AlarmEventSettings";
import MaskSettings from "Components/ConfigLayout/Camera/MaskSettings";
import AiAnalytics from "Components/ConfigLayout/Camera/AiAnalytics";
import SnmpSettings from "Components/ConfigLayout/Network/SnmpSettings";
import EmailFtpSettings from "Components/ConfigLayout/Event/EmailFtpSettings";
import ImageSettings from "Components/ConfigLayout/Camera/ImageSettings";
import MotionSettings from "Components/ConfigLayout/Camera/MotionSettings";
import MulticastSettings from "Components/ConfigLayout/Network/MulticastSettings";
import ScheduleSettings from "Components/ConfigLayout/System/ScheduleSettings";
import RoiSettings from "Components/ConfigLayout/Camera/RoiSettings";
import PageLayout from "Components/PageLayout";
import DynamicZoomSettings from "Components/ConfigLayout/Camera/DynamicZoomSettings";
import { useAppSelector } from "Data/Redux/Store";
import { selectSystemInformation } from "Data/Redux/Slices/Settings/System/Information";
import OverlaySettings from "Components/ConfigLayout/Camera/OverlaySettings";

function Router() {
	const { model } = useAppSelector(selectSystemInformation);
	const isPvm = model.includes("PV");
	const isDevelop = window.location.hostname === "localhost"
		|| window.location.hostname.includes("clintonelectronics.dev");

	const showDynamicZoom = isPvm || isDevelop;
	const router = createBrowserRouter(
		createRoutesFromElements(
			<Route path="/" element={ <PageLayout /> }>
				<Route path="login" element={ <LoginPage /> } />
				<Route path="/" element={ <PrivateRoute /> }>
					<Route index element={ <LiveLayout /> } />
					<Route path="/search" element={ <PlaybackLayout /> } />
					<Route path="/setup" element={ <ConfigLayout /> }>
						<Route index element={ <Navigate to="/setup/camera/codec" /> } />
						<Route path="/setup/camera">
							<Route index element={ <Navigate to="codec" /> } />
							<Route path="codec" element={ <CodecSettings /> } />
							<Route path="image" element={ <ImageSettings /> } />
							<Route path="roi" element={ <RoiSettings /> } />
							<Route path="motion" element={ <MotionSettings /> } />
							<Route path="mask" element={ <MaskSettings /> } />
							<Route path="embedded_osd" element={ <EmbeddedOsdSettings /> } />
							<Route path="overlays" element={ <OverlaySettings /> } />
							<Route path="tamper" element={ <TamperSettings /> } />
							<Route path="analytics" element={ <AiAnalytics /> } />
							{ showDynamicZoom && <Route path="dynamic_zoom" element={ <DynamicZoomSettings /> } /> }
						</Route>
						<Route path="/setup/audio">
							<Route index element={ <Navigate to="audio" /> } />
							<Route path="audio" element={ <AudioSettings /> } />
						</Route>
						<Route path="/setup/user">
							<Route index element={ <Navigate to="management" /> } />
							<Route path="management" element={ <UserManagement /> } />
							<Route path="authority" element={ <GroupAuthority /> } />
						</Route>
						<Route path="/setup/network">
							<Route index element={ <Navigate to="ip_setup" /> } />
							<Route path="ip_setup" element={ <IpSetupSettings /> } />
							<Route path="upnp" element={ <UpnpSettings /> } />
							<Route path="multicast" element={ <MulticastSettings /> } />
							<Route path="security" element={ <NetworkSecuritySettings /> } />
							<Route path="snmp" element={ <SnmpSettings /> } />
						</Route>
						<Route path="/setup/system">
							<Route index element={ <Navigate to="datetime" /> } />
							<Route path="datetime" element={ <DateTimeSettings /> } />
							<Route path="management" element={ <ManagementSettings /> } />
							<Route path="information" element={ <SystemInformation /> } />
							<Route path="language" element={ <LanguageSettings /> } />
							<Route path="security" element={ <SecuritySettings /> } />
							<Route path="schedule" element={ <ScheduleSettings /> } />
						</Route>
						<Route path="/setup/storage">
							<Route index element={ <Navigate to="sd_card" /> } />
							<Route path="sd_card" element={ <SdCardSettings /> } />
						</Route>
						<Route path="/setup/event">
							<Route index element={ <Navigate to="action" /> } />
							<Route path="action" element={ <ActionSettings /> } />
							<Route path="emailftp" element={ <EmailFtpSettings /> } />
							<Route path="alarm" element={ <AlarmEventSettings /> } />
							<Route path="motion" element={ <MotionEventSettings /> } />
							<Route path="tamper" element={ <TamperEventSettings /> } />
							<Route path="network_loss" element={ <NetworkLossSettings /> } />
							<Route path="system" element={ <SystemEventSettings /> } />
							<Route path="ai_analytics" element={ <AnalyticsSettings /> } />
						</Route>
						<Route path="/setup/record">
							<Route index element={ <Navigate to="setup" /> } />
							<Route path="setup" element={ <RecordSetupSettings /> } />
						</Route>
					</Route>
				</Route>
			</Route>
		)
	);

	return <RouterProvider router={ router } />;
}

export default memo(Router);
